import React, { useEffect, useState } from "react"
import {
    Box,
    Button,
    FormControlLabel,
    Grid,
    Paper,
    Switch,
    TextField,
    Typography,
} from "@material-ui/core"
import SaveIcon from "@material-ui/icons/Save"
import { useSnackbar } from "../../../utils/notificacoes"
import { obterUber, salvarUber, type IntegracaoUber } from "../../../services/integracaoParceiros"
import useLoadingStore from "../../../stores/LoadingStore"
import { useIntegracaoStyles } from "../../../utils/integracaoParceirosStyles"

const inicial: IntegracaoUber = {
    clienteId: "",
    secretId: "",
    costumerId: "",
    observacaoRetirada: "",
    minutosChamarEntregador: null,
    retornarItemNaoEntregue: false,
    automatico: false,
    ativo: false,
}

export function ParametroIntegracaoUberForm() {
    const classes = useIntegracaoStyles()
    const { enqueueSnackbar } = useSnackbar()
    const setLoading = useLoadingStore(s => s.setLoading)

    const [dados, setDados] = useState<IntegracaoUber>(inicial)

    const alertStart = (msg: string, tipo: "success" | "error" | "info") =>
        enqueueSnackbar(msg, { variant: tipo })

    const setCampo = <K extends keyof IntegracaoUber>(campo: K, valor: IntegracaoUber[K]) =>
        setDados(prev => ({ ...prev, [campo]: valor }))

    const normalizar = (res: IntegracaoUber): IntegracaoUber => ({
        clienteId: res.clienteId ?? "",
        secretId: res.secretId ?? "",
        costumerId: res.costumerId ?? "",
        observacaoRetirada: res.observacaoRetirada ?? "",
        minutosChamarEntregador: res.minutosChamarEntregador,
        retornarItemNaoEntregue: res.retornarItemNaoEntregue,
        automatico: res.automatico,
        ativo: res.ativo,
    })

    useEffect(() => {
        const carregar = async () => {
            const autorizacao = localStorage.getItem("chaveSistema")
            if (!autorizacao) return
            setLoading(true)
            try {
                const res = await obterUber(autorizacao)
                if ("retornoErro" in res) {
                    alertStart(res.mensagem || "Erro ao carregar integração", "error")
                } else {
                    setDados(normalizar(res))
                }
            } finally {
                setLoading(false)
            }
        }
        carregar()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleSalvar = async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        setLoading(true)
        try {
            const res = await salvarUber(autorizacao, dados)
            if ("retornoErro" in res) {
                alertStart(res.mensagem || "Erro ao salvar", "error")
            } else {
                setDados(normalizar(res))
                alertStart("Configuração salva com sucesso!", "success")
            }
        } finally {
            setLoading(false)
        }
    }

    const textos: { campo: keyof IntegracaoUber; label: string }[] = [
        { campo: "clienteId", label: "Cliente ID" },
        { campo: "secretId", label: "Secret ID" },
        { campo: "costumerId", label: "Costumer ID" },
        { campo: "observacaoRetirada", label: "Observação para retirada" },
    ]

    const switches: { campo: keyof IntegracaoUber; label: string }[] = [
        { campo: "retornarItemNaoEntregue", label: "Retornar com item não entregue" },
        { campo: "automatico", label: "Alocar automaticamente o entregador" },
        { campo: "ativo", label: "Ativo" },
    ]

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Typography variant="h5" className={classes.titleText}>
                    Integração Uber
                </Typography>

                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Parâmetros Uber
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Grid container spacing={3}>
                            {textos.map(t => (
                                <Grid item xs={12} sm={6} md={4} key={t.campo}>
                                    <TextField
                                        label={t.label}
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={(dados[t.campo] as string) ?? ""}
                                        onChange={e => setCampo(t.campo, e.target.value as never)}
                                    />
                                </Grid>
                            ))}

                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    label="Minutos para chamar o entregador"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    type="number"
                                    inputProps={{ min: 0, step: 1 }}
                                    value={dados.minutosChamarEntregador ?? ""}
                                    onChange={e => {
                                        const v = e.target.value
                                        setCampo("minutosChamarEntregador", v === "" ? null : Number(v))
                                    }}
                                />
                            </Grid>

                            {switches.map(s => (
                                <Grid item xs={12} sm={6} md={4} key={s.campo}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={dados[s.campo] as boolean}
                                                onChange={e => {
                                                    const checked = e.target.checked
                                                    setCampo(s.campo, checked as never)
                                                }}
                                            />
                                        }
                                        label={s.label}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </Paper>

                <Box display="flex" justifyContent="center" mb={4}>
                    <Button
                        className={classes.saveButton}
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSalvar}
                    >
                        Salvar
                    </Button>
                </Box>
            </Box>
        </Box>
    )
}
